import { useTranslation } from 'react-i18next';
import { Check, Loader2, Mail, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { useAcceptInvitation, useDeclineInvitation, useFetchMyInvitations } from '@/hooksApi';

/**
 * "You've been invited to {workspace}" — for somebody who ALREADY has a
 * workspace and is working in it.
 *
 * ⚠️ A BANNER, NOT THE LANDING SCREEN. A person mid-session has somewhere to
 * be; taking the whole page over to ask them about an invitation would
 * interrupt work they were doing to answer a question that can wait. Somebody
 * with NO workspace has nowhere else to be, which is why that case gets the
 * full screen (NoWorkspaceLanding) and this one gets a strip.
 *
 * The sidebar already carries a COUNT. A count tells you something happened; it
 * does not let you do anything about it, and the owner asked for Accept and
 * Decline to be right there.
 *
 * Accepting JOINS, it does not switch — the same rule InvitationsPanel states.
 * Pulling the ground out from under an open page is a second, deliberate click.
 */
const PendingInvitationBanner = () => {
  const { t } = useTranslation();
  const { data } = useFetchMyInvitations();
  const accept = useAcceptInvitation();
  const decline = useDeclineInvitation();

  // The server returns OPEN invitations only, so no client-side filtering —
  // rendering an expired one produces a prompt nobody can clear.
  const invitation = (data?.object ?? [])[0];

  if (!invitation) {
    return null;
  }

  const workspaceName = invitation.workspace?.name || String(t('workspace.untitled'));
  const busy = accept.isLoading || decline.isLoading;

  const onAccept = () =>
    accept.mutate(invitation.uid, {
      onSuccess: () => toast({ title: String(t('workspace.accepted', { name: workspaceName })) }),
      onError: () => toast({ title: String(t('workspace.invite_failed')), variant: 'destructive' }),
    });

  const onDecline = () =>
    decline.mutate(invitation.uid, {
      onSuccess: () => toast({ title: String(t('workspace.declined')) }),
      onError: () => toast({ title: String(t('workspace.invite_failed')), variant: 'destructive' }),
    });

  return (
    <div
      className='mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-customGreen/40 bg-customGreen/[0.06] px-4 py-3'
      role='status'
    >
      <Mail className='h-4 w-4 shrink-0 text-customGreen' aria-hidden />
      <span className='flex-1 text-sm text-sd-foreground'>
        {/*
          ⚠️ ONE INTERPOLATED SENTENCE, NOT PIECES CONCATENATED AROUND A NAME.
          `workspace.invited_you` is the FRAGMENT "invited you to join", built
          to sit between an inviter and a workspace; composing a sentence out of
          it here would put the parts in English word order and read backwards
          in Arabic.
        */}
        {String(t('workspace.banner_invited', { name: workspaceName }))}
      </span>
      <div className='flex items-center gap-2'>
        <Button size='sm' variant='ghost' onClick={onDecline} disabled={busy}>
          <X className='me-1 h-3.5 w-3.5' aria-hidden />
          {String(t('workspace.decline'))}
        </Button>
        <Button size='sm' onClick={onAccept} disabled={busy}>
          {accept.isLoading ? (
            <Loader2 className='me-1 h-3.5 w-3.5 animate-spin' aria-hidden />
          ) : (
            <Check className='me-1 h-3.5 w-3.5' aria-hidden />
          )}
          {String(t('workspace.accept'))}
        </Button>
      </div>
    </div>
  );
};

export default PendingInvitationBanner;
